/**
 * Contrôleur de thème — clair / sombre / système.
 *
 * Le choix explicite pose `data-theme` sur <html> (consommé par tokens.css et par la
 * variante `dark:` de Tailwind) ; « système » retire l'attribut et laisse
 * `prefers-color-scheme` décider.
 *
 * U6b vague 10 : le choix est mémorisé PAR UTILISATEUR (`doit-theme:<utilisateur>`), et
 * « système » est la valeur par défaut d'un utilisateur qui n'a jamais choisi. La clé
 * globale `doit-theme` ne garde que le DERNIER thème appliqué sur cet appareil, lu une fois
 * au démarrage avant la session (évite le flash) ; la coquille réapplique ensuite le choix
 * de l'utilisateur connecté. Tout accès au stockage est protégé (navigation privée, quota).
 */
export type Theme = 'light' | 'dark' | 'system'

const KEY = 'doit-theme'

export function cleTheme(utilisateur?: string): string {
  return utilisateur ? `${KEY}:${utilisateur}` : KEY
}

function valide(t: string | null): t is Theme {
  return t === 'light' || t === 'dark' || t === 'system'
}

export function getTheme(utilisateur?: string): Theme {
  try {
    const t = localStorage.getItem(cleTheme(utilisateur))
    if (valide(t)) return t
  } catch {
    /* stockage indisponible : on retombe sur « système » */
  }
  return 'system'
}

export function applyTheme(theme: Theme): void {
  const root = document.documentElement
  if (theme === 'system') root.removeAttribute('data-theme')
  else root.setAttribute('data-theme', theme)
}

export function setTheme(theme: Theme, utilisateur?: string): void {
  for (const cle of new Set([cleTheme(utilisateur), KEY])) {
    try {
      if (theme === 'system') localStorage.removeItem(cle)
      else localStorage.setItem(cle, theme)
    } catch {
      /* ignore : le thème s'appliquera quand même pour la session courante */
    }
  }
  applyTheme(theme)
}

/** Applique le dernier thème de l'appareil au démarrage, avant le premier rendu (évite le flash). */
export function initTheme(): void {
  applyTheme(getTheme())
}
